<template>
  <div class="carousel-page">

    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Gestão de publicidades</div>
          <div class="page-subtitle">{{ sortedItems.length }} campanha{{ sortedItems.length !== 1 ? 's' : '' }} configurada{{ sortedItems.length !== 1 ? 's' : '' }}</div>
        </div>
        <button class="add-btn" @click="openCreate">
          <q-icon name="add" size="18px" class="q-mr-xs" />
          Novo Slide
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">

      <!-- Loading skeletons -->
      <div v-if="loading" class="row q-col-gutter-lg">
        <div v-for="n in 6" :key="n" class="col-12 col-sm-6 col-md-4">
          <div class="skeleton-card">
            <q-skeleton height="160px" />
            <div class="q-pa-md">
              <q-skeleton type="text" width="70%" class="q-mb-sm" />
              <q-skeleton type="text" width="50%" />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loading" class="campaign-filters">
        <q-select
          v-model="placementFilter"
          :options="placementFilterOptions"
          emit-value
          map-options
          clearable
          outlined
          dense
          label="Todas as posições"
        />
        <q-select
          v-model="statusFilter"
          :options="statusOptions"
          emit-value
          map-options
          outlined
          dense
          label="Estado"
        />
      </div>

      <!-- Empty state -->
      <div v-if="!loading && filteredItems.length === 0" class="empty-state">
        <div class="empty-icon-wrap">
          <q-icon name="view_carousel" size="48px" color="grey-4" />
        </div>
        <div class="empty-title">Nenhum slide configurado</div>
        <div class="empty-sub">Ajuste os filtros ou crie a primeira campanha publicitária.</div>
        <button class="add-btn q-mt-lg" @click="openCreate">
          <q-icon name="add" size="18px" class="q-mr-xs" />
          Novo Slide
        </button>
      </div>

      <!-- Grid -->
      <div v-if="!loading && filteredItems.length" class="row q-col-gutter-lg">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="col-12 col-sm-6 col-md-4"
        >
          <div class="slide-card">
            <!-- Image -->
            <div class="slide-img-wrap">
              <q-img :src="primaryCreative(item)?.image_url" :ratio="creativeRatio(primaryCreative(item))" class="slide-img">
                <template v-slot:loading>
                  <q-spinner-gears color="white" size="28px" />
                </template>
              </q-img>
              <!-- Badges overlay -->
              <div class="slide-badges">
                <span class="order-badge">#{{ item.order }}</span>
                <span class="status-badge" :class="item.active ? 'status-active' : 'status-inactive'">
                  {{ item.active ? 'Ativo' : 'Inativo' }}
                </span>
              </div>
              <button
                v-if="campaignImages(item).length > 1"
                type="button"
                class="images-badge"
                @click.stop="openGallery(item)"
              >
                <q-icon name="collections" size="14px" />
                {{ campaignImages(item).length }} imagens
                <q-tooltip>Ver todas as imagens da campanha</q-tooltip>
              </button>
            </div>

            <!-- Info -->
            <div class="slide-info">
              <div class="slide-title">{{ item.title || '(sem título)' }}</div>
              <div class="slide-link" v-if="item.link">
                <q-icon name="link" size="12px" />
                <span>{{ item.link }}</span>
              </div>
              <div class="slide-link slide-link--empty" v-else>Sem link</div>
              <div class="campaign-placements">
                <span v-for="creative in item.creatives" :key="creative.placement_key">
                  {{ creative.placement?.label || creative.placement_key }}
                </span>
              </div>
              <div v-if="item.creatives.some(creative => creative.mobile_image_url)" class="mobile-indicator">
                <q-icon name="phone_android" size="12px" />
                <span>Inclui criativo mobile</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="slide-actions">
              <button class="slide-action-btn slide-action-btn--neutral" @click="openGallery(item)">
                <q-icon name="photo_library" size="16px" />
                <q-tooltip>Ver imagens</q-tooltip>
              </button>
              <button
                class="slide-action-btn"
                :class="item.active ? 'slide-action-btn--warn' : 'slide-action-btn--success'"
                :disabled="togglingId === item.id"
                @click="toggleActive(item)"
              >
                <q-spinner-dots v-if="togglingId === item.id" size="16px" />
                <q-icon v-else :name="item.active ? 'visibility_off' : 'visibility'" size="16px" />
                <q-tooltip>{{ item.active ? 'Desativar' : 'Ativar' }}</q-tooltip>
              </button>
              <button class="slide-action-btn slide-action-btn--primary" @click="openEdit(item)">
                <q-icon name="edit" size="16px" />
                <q-tooltip>Editar</q-tooltip>
              </button>
              <button class="slide-action-btn slide-action-btn--danger" @click="confirmDelete(item)">
                <q-icon name="delete" size="16px" />
                <q-tooltip>Eliminar</q-tooltip>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit Dialog ── -->
    <q-dialog v-model="dialog" persistent>
      <q-card class="dialog-card dialog-card--campaign">
        <div class="dialog-header">
          <div class="dialog-title">{{ isEditing ? 'Editar Slide' : 'Novo Slide' }}</div>
          <button class="dialog-close" @click="closeDialog">
            <q-icon name="close" size="20px" />
          </button>
        </div>

        <div class="dialog-body">
          <q-form @submit.prevent="submitForm" ref="formRef">

            <div class="field-wrap">
              <label class="field-label">Posições de exibição</label>
              <q-option-group
                v-model="form.placementKeys"
                :options="placementOptions"
                type="checkbox"
                color="positive"
                @update:model-value="syncCreativeForms"
              />
            </div>

            <section
              v-for="placementKey in form.placementKeys"
              :key="placementKey"
              class="creative-editor"
            >
              <div class="creative-editor__heading">
                <strong>{{ placementByKey(placementKey)?.label || placementKey }}</strong>
                <span>{{ formatLabel(placementByKey(placementKey)?.format) }}</span>
              </div>
              <p class="creative-editor__hint">
                Recomendado: {{ placementByKey(placementKey)?.width }} × {{ placementByKey(placementKey)?.height }} px.
                Mobile: {{ placementDimensionLabel(placementByKey(placementKey), true) }}.
                JPG, PNG ou WebP até 5 MB e 8000 × 8000 px; proporção validada no servidor.
              </p>

              <div class="field-wrap">
                <label class="field-label">Imagem desktop</label>
                <q-file
                  v-model="form.creatives[placementKey].image"
                  outlined
                  accept=".jpg,.jpeg,.png,.webp"
                  max-file-size="5242880"
                  :rules="isNewCreative(placementKey) ? [val => !!val || 'A imagem é obrigatória para esta posição'] : []"
                  class="dialog-input"
                  @update:model-value="previewFile(placementKey, 'image', $event)"
                >
                  <template v-slot:prepend><q-icon name="upload_file" size="18px" /></template>
                  <template v-slot:hint>
                    <span>{{ isNewCreative(placementKey) ? 'Obrigatória para nova posição' : 'Vazio mantém a imagem actual' }}</span>
                  </template>
                </q-file>
              </div>
              <template v-if="creativePreview(placementKey, 'image') || existingCreative(placementKey)?.image_url">
                <span class="creative-preview-label">
                  {{ creativePreview(placementKey, 'image') ? 'Nova imagem desktop' : 'Imagem desktop actual' }}
                </span>
                <q-img
                  :src="creativePreview(placementKey, 'image') || existingCreative(placementKey).image_url"
                  :ratio="placementRatio(placementByKey(placementKey))"
                  class="creative-preview"
                />
              </template>

              <div class="field-wrap">
                <label class="field-label">
                  Imagem mobile <span class="field-optional">opcional</span>
                </label>
                <q-file
                  v-model="form.creatives[placementKey].mobileImage"
                  outlined
                  accept=".jpg,.jpeg,.png,.webp"
                  max-file-size="5242880"
                  class="dialog-input"
                  @update:model-value="previewFile(placementKey, 'mobileImage', $event)"
                >
                  <template v-slot:prepend><q-icon name="phone_android" size="18px" /></template>
                  <template v-slot:hint>Sem alternativa mobile, a imagem desktop adapta-se à proporção do slot.</template>
                </q-file>
                <button
                  v-if="existingCreative(placementKey)?.mobile_image_url && !form.creatives[placementKey].removeMobile"
                  type="button"
                  class="remove-mobile-btn"
                  @click="form.creatives[placementKey].removeMobile = true"
                >Remover imagem mobile actual</button>
                <span v-if="form.creatives[placementKey].removeMobile" class="text-negative">
                  A imagem mobile será removida ao guardar.
                  <button type="button" class="undo-remove-btn" @click="form.creatives[placementKey].removeMobile = false">Desfazer</button>
                </span>
              </div>
              <template v-if="creativePreview(placementKey, 'mobileImage')">
                <span class="creative-preview-label">Nova imagem mobile</span>
                <q-img
                  :src="creativePreview(placementKey, 'mobileImage')"
                  :ratio="placementMobileRatio(placementByKey(placementKey))"
                  class="creative-preview"
                />
              </template>
              <template v-else-if="existingCreative(placementKey)?.mobile_image_url && !form.creatives[placementKey].removeMobile">
                <span class="creative-preview-label">Imagem mobile actual</span>
                <q-img
                  :src="existingCreative(placementKey).mobile_image_url"
                  :ratio="placementMobileRatio(placementByKey(placementKey))"
                  class="creative-preview"
                />
              </template>
            </section>

            <!-- Title -->
            <div class="field-wrap">
              <label class="field-label">Título <span class="field-optional">opcional</span></label>
              <q-input
                v-model="form.title"
                outlined
                dense
                placeholder="Ex: Imóveis de Luxo em Maputo"
                maxlength="255"
                class="dialog-input"
              >
                <template v-slot:prepend><q-icon name="title" size="18px" /></template>
              </q-input>
            </div>

            <div class="field-wrap">
              <label class="field-label">Texto alternativo <span class="field-optional">opcional</span></label>
              <q-input v-model="form.altText" outlined dense maxlength="255" class="dialog-input" />
            </div>

            <!-- Link -->
            <div class="field-wrap">
              <label class="field-label">Link <span class="field-optional">opcional</span></label>
              <q-input
                v-model="form.link"
                outlined
                dense
                placeholder="https://..."
                maxlength="500"
                :rules="[val => !val || /^https?:\/\/.+/.test(val) || 'Insira um URL válido']"
                class="dialog-input"
              >
                <template v-slot:prepend><q-icon name="link" size="18px" /></template>
              </q-input>
            </div>

            <div class="row q-col-gutter-md">
              <div class="col-6">
                <q-input v-model="form.startsAt" type="datetime-local" outlined dense label="Início (opcional)" class="dialog-input" />
              </div>
              <div class="col-6">
                <q-input v-model="form.endsAt" type="datetime-local" outlined dense label="Fim (opcional)" class="dialog-input" />
              </div>
            </div>

            <!-- Order + Active -->
            <div class="row q-col-gutter-md">
              <div class="col-6">
                <div class="field-wrap">
                  <label class="field-label">Ordem</label>
                  <q-input
                    v-model.number="form.order"
                    type="number"
                    outlined
                    dense
                    :rules="[val => val >= 0 || 'Valor mínimo: 0']"
                    class="dialog-input"
                  >
                    <template v-slot:prepend><q-icon name="swap_vert" size="18px" /></template>
                  </q-input>
                </div>
              </div>
              <div class="col-6 flex items-end" style="padding-bottom: 2px;">
                <div class="toggle-wrap">
                  <q-toggle v-model="form.active" color="positive" class="dialog-toggle" />
                  <span class="toggle-label">{{ form.active ? 'Slide activo' : 'Slide inactivo' }}</span>
                </div>
              </div>
            </div>

          </q-form>
        </div>

        <div class="dialog-footer">
          <button class="dialog-btn dialog-btn--cancel" @click="closeDialog">Cancelar</button>
          <button class="dialog-btn dialog-btn--submit" :disabled="saving" @click="submitForm">
            <q-spinner-dots v-if="saving" size="16px" class="q-mr-xs" />
            {{ isEditing ? 'Guardar' : 'Criar' }}
          </button>
        </div>
      </q-card>
    </q-dialog>

    <!-- ── Gallery Dialog ── -->
    <q-dialog v-model="galleryDialog">
      <q-card class="dialog-card dialog-card--gallery">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">Imagens da campanha</div>
            <div class="gallery-subtitle">
              {{ galleryItem?.title || '(sem título)' }} · {{ galleryImages.length }} {{ galleryImages.length !== 1 ? 'imagens' : 'imagem' }}
            </div>
          </div>
          <button class="dialog-close" @click="galleryDialog = false">
            <q-icon name="close" size="20px" />
          </button>
        </div>
        <div class="dialog-body">
          <div class="gallery-grid">
            <figure v-for="image in galleryImages" :key="image.key" class="gallery-item">
              <q-img :src="image.url" :ratio="image.ratio" fit="contain" class="gallery-img">
                <template v-slot:loading>
                  <q-spinner-gears color="grey-5" size="24px" />
                </template>
              </q-img>
              <figcaption class="gallery-caption">
                <span class="gallery-placement">{{ image.placementLabel }}</span>
                <span class="gallery-device" :class="{ 'gallery-device--mobile': image.mobile }">
                  <q-icon :name="image.mobile ? 'phone_android' : 'desktop_windows'" size="12px" />
                  {{ image.mobile ? 'Mobile' : 'Desktop' }}
                </span>
                <span class="gallery-dimensions">{{ image.dimensions }}</span>
              </figcaption>
            </figure>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn dialog-btn--cancel" @click="galleryDialog = false">Fechar</button>
          <button class="dialog-btn dialog-btn--submit" @click="editFromGallery">
            <q-icon name="edit" size="16px" class="q-mr-xs" />
            Editar campanha
          </button>
        </div>
      </q-card>
    </q-dialog>

    <!-- ── Delete Dialog ── -->
    <q-dialog v-model="deleteDialog" persistent>
      <q-card class="dialog-card" style="max-width: 380px;">
        <div class="dialog-header">
          <div class="dialog-title">Eliminar Slide</div>
          <button class="dialog-close" @click="deleteDialog = false">
            <q-icon name="close" size="20px" />
          </button>
        </div>
        <div class="dialog-body">
          <div class="delete-warning">
            <q-icon name="warning" size="28px" color="negative" class="q-mr-sm" />
            <span>Esta acção é permanente e não pode ser revertida.</span>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn dialog-btn--cancel" @click="deleteDialog = false">Cancelar</button>
          <button class="dialog-btn dialog-btn--danger" :disabled="deleting" @click="doDelete">
            <q-spinner-dots v-if="deleting" size="16px" class="q-mr-xs" />
            Eliminar
          </button>
        </div>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import store from '@/vuex/store';
import { useQuasar } from 'quasar';

const $q = useQuasar();

const loading   = ref(true);
const saving    = ref(false);
const deleting  = ref(false);
const togglingId = ref(null);
const dialog      = ref(false);
const deleteDialog = ref(false);
const isEditing   = ref(false);
const formRef     = ref(null);
const targetItem  = ref(null);
const placements = ref([]);
const previews = ref({});
const placementFilter = ref(null);
const statusFilter = ref('all');
const galleryDialog = ref(false);
const galleryItem = ref(null);
const galleryImages = computed(() => campaignImages(galleryItem.value));

const items = computed(() => store.state.carousel.items);
const sortedItems = computed(() =>
  [...items.value].sort((a, b) => a.order - b.order)
);
const filteredItems = computed(() => sortedItems.value.filter((item) => {
  const hasPlacement = !placementFilter.value ||
    item.creatives.some((creative) => creative.placement_key === placementFilter.value);
  const hasStatus = statusFilter.value === 'all' ||
    item.active === (statusFilter.value === 'active');
  return hasPlacement && hasStatus;
}));
const placementOptions = computed(() => placements.value.map((placement) => ({
  label: `${placement.label} — ${placement.width} × ${placement.height}px`,
  value: placement.key,
})));
const placementFilterOptions = computed(() => [
  { label: 'Todas as posições', value: null },
  ...placementOptions.value,
]);
const statusOptions = [
  { label: 'Todos os estados', value: 'all' },
  { label: 'Activas', value: 'active' },
  { label: 'Inactivas', value: 'inactive' },
];

const emptyForm = () => ({
  title: '', altText: '', link: '', order: 0, active: true,
  startsAt: '', endsAt: '', placementKeys: [], creatives: {},
});

const form = ref(emptyForm());

function placementByKey(key) {
  return placements.value.find((placement) => placement.key === key);
}

function existingCreative(key) {
  return targetItem.value?.creatives.find((creative) => creative.placement_key === key) || null;
}

function isNewCreative(key) {
  return !isEditing.value || !existingCreative(key);
}

function primaryCreative(item) {
  return item?.creatives?.[0] || null;
}

// Uma entrada por imagem colocada (desktop e mobile de cada posição)
function campaignImages(item) {
  return (item?.creatives || []).flatMap((creative) => {
    const placement = creative.placement || placementByKey(creative.placement_key);
    const placementLabel = placement?.label || creative.placement_key;
    const images = [];
    if (creative.image_url) {
      images.push({
        key: `${creative.placement_key}:desktop`, url: creative.image_url, placementLabel, mobile: false,
        ratio: placementRatio(placement), dimensions: placementDimensionLabel(placement),
      });
    }
    if (creative.mobile_image_url) {
      images.push({
        key: `${creative.placement_key}:mobile`, url: creative.mobile_image_url, placementLabel, mobile: true,
        ratio: placementMobileRatio(placement), dimensions: placementDimensionLabel(placement, true),
      });
    }
    return images;
  });
}

function openGallery(item) {
  galleryItem.value = item;
  galleryDialog.value = true;
}

function editFromGallery() {
  const item = galleryItem.value;
  galleryDialog.value = false;
  if (item) openEdit(item);
}

function placementRatio(placement) {
  return placement?.width && placement?.height ? placement.width / placement.height : 16 / 7;
}

function placementMobileRatio(placement) {
  return placement?.mobile_width && placement?.mobile_height
    ? placement.mobile_width / placement.mobile_height
    : placementRatio(placement);
}

function placementDimensionLabel(placement, mobile = false) {
  const width = mobile ? placement?.mobile_width : placement?.width;
  const height = mobile ? placement?.mobile_height : placement?.height;
  if (!width || !height) return 'igual à imagem desktop';
  if (mobile && width <= 32 && height <= 32) return `${width}:${height} (proporção)`;
  return `${width} × ${height} px`;
}

function creativeRatio(creative) {
  return placementRatio(placementByKey(creative?.placement_key));
}

function formatLabel(format) {
  return {
    banner: 'Banner',
    inline_banner: 'Banner entre resultados',
    sidebar_card: 'Cartão lateral',
    partner_card: 'Cartão de parceiro',
  }[format] || format || 'Publicidade';
}

function syncCreativeForms(keys) {
  keys.forEach((key) => {
    if (!form.value.creatives[key]) {
      form.value.creatives[key] = { image: null, mobileImage: null, removeMobile: false };
    }
  });
}

function previewFile(key, type, file) {
  if (!file) {
    delete previews.value[`${key}:${type}`];
    return;
  }
  const reader = new FileReader();
  reader.onload = (event) => {
    previews.value[`${key}:${type}`] = event.target.result;
  };
  reader.onerror = () => {
    $q.notify({ type: 'negative', message: 'Não foi possível pré-visualizar esta imagem.' });
  };
  reader.readAsDataURL(file);
  if (type === 'mobileImage') form.value.creatives[key].removeMobile = false;
}

function creativePreview(key, type) {
  return previews.value[`${key}:${type}`] || null;
}

function openCreate() {
  isEditing.value = false;
  targetItem.value = null;
  form.value = emptyForm();
  previews.value = {};
  dialog.value = true;
}

function openEdit(item) {
  isEditing.value = true;
  targetItem.value = item;
  form.value = {
    title: item.title ?? '', altText: item.alt_text ?? '', link: item.link ?? '', order: item.order ?? 0,
    active: item.active, startsAt: toDateTimeLocal(item.starts_at), endsAt: toDateTimeLocal(item.ends_at),
    placementKeys: item.creatives.map((creative) => creative.placement_key),
    creatives: Object.fromEntries(item.creatives.map((creative) => [
      creative.placement_key,
      { image: null, mobileImage: null, removeMobile: false },
    ])),
  };
  previews.value = {};
  dialog.value = true;
}

function toDateTimeLocal(value) {
  if (!value) return '';
  return value.replace(' ', 'T').slice(0, 16);
}

function closeDialog() {
  dialog.value = false;
  form.value = emptyForm();
  previews.value = {};
}

async function submitForm() {
  if (saving.value) return;
  const valid = await formRef.value?.validate();
  if (!valid) return;
  if (!form.value.placementKeys.length) {
    $q.notify({ type: 'negative', message: 'Seleccione pelo menos uma posição de exibição.' });
    return;
  }

  const fd = new FormData();
  form.value.placementKeys.forEach((key) => {
    fd.append('placement_keys[]', key);
    const creative = form.value.creatives[key];
    if (creative.image) fd.append(`creative_images[${key}]`, creative.image);
    if (creative.mobileImage) fd.append(`mobile_images[${key}]`, creative.mobileImage);
    if (creative.removeMobile) fd.append(`remove_mobile_image[${key}]`, '1');
  });
  fd.append('title', form.value.title);
  fd.append('alt_text', form.value.altText);
  fd.append('link', form.value.link);
  fd.append('starts_at', form.value.startsAt);
  fd.append('ends_at', form.value.endsAt);
  fd.append('order', String(form.value.order));
  fd.append('active', form.value.active ? '1' : '0');

  saving.value = true;
  try {
    if (isEditing.value) {
      await store.dispatch('carousel/updateCarousel', { id: targetItem.value.id, formData: fd });
      $q.notify({ type: 'positive', message: 'Campanha actualizada com sucesso.' });
    } else {
      await store.dispatch('carousel/createCarousel', fd);
      $q.notify({ type: 'positive', message: 'Campanha criada com sucesso.' });
    }
    closeDialog();
  } catch (err) {
    const errors = err.response?.data?.errors;
    const details = errors
      ? Object.entries(errors)
        .map(([field, messages]) => `${field}: ${[].concat(messages).join(' ')}`)
        .join('\n')
      : '';
    const msg = details || err.response?.data?.message || 'Erro ao guardar o slide.';
    $q.notify({ type: 'negative', message: msg });
  } finally {
    saving.value = false;
  }
}

function confirmDelete(item) {
  targetItem.value = item;
  deleteDialog.value = true;
}

async function doDelete() {
  if (deleting.value) return;
  deleting.value = true;
  try {
    await store.dispatch('carousel/deleteCarousel', targetItem.value.id);
    $q.notify({ type: 'positive', message: 'Campanha eliminada.' });
    deleteDialog.value = false;
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Erro ao eliminar a campanha.' });
  } finally {
    deleting.value = false;
  }
}

async function toggleActive(item) {
  togglingId.value = item.id;
  const fd = new FormData();
  fd.append('title', item.title || '');
  fd.append('alt_text', item.alt_text || '');
  fd.append('link', item.link || '');
  fd.append('order', String(item.order || 0));
  fd.append('starts_at', item.starts_at || '');
  fd.append('ends_at', item.ends_at || '');
  item.creatives.forEach((creative) => fd.append('placement_keys[]', creative.placement_key));
  fd.append('active', item.active ? '0' : '1');
  try {
    await store.dispatch('carousel/updateCarousel', { id: item.id, formData: fd });
    $q.notify({ type: 'positive', message: 'Estado da campanha actualizado.' });
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Erro ao alterar estado da campanha.' });
  } finally {
    togglingId.value = null;
  }
}

onMounted(async () => {
  try {
    const [availablePlacements] = await Promise.all([
      store.dispatch('carousel/getPlacements'),
      store.dispatch('carousel/getCarousel'),
    ]);
    placements.value = availablePlacements;
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível carregar as campanhas e posições.' });
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.carousel-page { background: #f4f6fb; min-height: 100vh; }

/* ── Header ───────────────────────────────────────────── */
.page-header { background: #0d1b2a; padding: 20px 24px; }
.page-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 1200px; margin: 0 auto; gap: 12px;
}
.page-title { font-size: 20px; font-weight: 700; color: #fff; }
.page-subtitle { font-size: 12px; color: rgba(212,175,55,.75); letter-spacing: .4px; margin-top: 2px; }

.add-btn {
  display: inline-flex; align-items: center;
  padding: 9px 20px; border-radius: 10px;
  background: #d4af37; color: #0d1b2a;
  font-size: 13px; font-weight: 700; border: none; cursor: pointer;
  transition: background .18s; letter-spacing: .3px;
}
.add-btn:hover { background: #c9a430; }

/* ── Content ──────────────────────────────────────────── */
.content-wrapper { max-width: 1200px; margin: 28px auto; padding: 0 20px 48px; }
.campaign-filters { display: grid; grid-template-columns: minmax(220px, 1fr) minmax(180px, .5fr); gap: 12px; margin-bottom: 20px; }

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-card { background: #fff; border-radius: 14px; overflow: hidden; border: 1px solid #e8ecf4; }

/* ── Empty ────────────────────────────────────────────── */
.empty-state {
  display: flex; flex-direction: column; align-items: center;
  padding: 80px 16px; text-align: center;
}
.empty-icon-wrap {
  width: 88px; height: 88px; background: #fff; border-radius: 24px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #e8ecf4; box-shadow: 0 4px 16px rgba(13,27,42,.06);
  margin-bottom: 20px;
}
.empty-title { font-size: 18px; font-weight: 700; color: #0f172a; }
.empty-sub { font-size: 13px; color: #94a3b8; margin-top: 6px; }

/* ── Slide card ───────────────────────────────────────── */
.slide-card {
  background: #fff; border-radius: 14px; overflow: hidden;
  border: 1px solid #e8ecf4;
  transition: transform .22s, box-shadow .22s;
}
.slide-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(13,27,42,.1);
}

.slide-img-wrap { position: relative; overflow: hidden; }
.slide-img { display: block; }
.slide-img-wrap:hover .slide-img { transform: scale(1.03); transition: transform .35s; }

.slide-badges {
  position: absolute; top: 10px; left: 10px; right: 10px;
  display: flex; justify-content: space-between; align-items: center;
}
.order-badge {
  padding: 3px 9px; border-radius: 20px;
  background: rgba(13,27,42,.7); color: #d4af37;
  font-size: 11px; font-weight: 700; backdrop-filter: blur(4px);
}
.status-badge {
  padding: 3px 9px; border-radius: 20px;
  font-size: 11px; font-weight: 700; backdrop-filter: blur(4px);
}
.images-badge {
  position: absolute; left: 10px; bottom: 10px;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border: 0; border-radius: 20px;
  background: rgba(13,27,42,.78); color: #fff;
  font-size: 11px; font-weight: 700; backdrop-filter: blur(4px); cursor: pointer;
}
.images-badge:hover { background: rgba(13,27,42,.92); color: #d4af37; }
.status-active  { background: rgba(22,163,74,.85);  color: #fff; }
.status-inactive { background: rgba(100,116,139,.75); color: #fff; }

.slide-info { padding: 14px 16px 12px; }
.slide-title {
  font-size: 13.5px; font-weight: 700; color: #0f172a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px;
}
.slide-link {
  display: flex; align-items: center; gap: 4px;
  font-size: 11.5px; color: #64748b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.slide-link--empty { color: #cbd5e1; }
.campaign-placements { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 9px; }
.campaign-placements span { padding: 3px 7px; border-radius: 12px; color: #334155; background: #eef2f7; font-size: 10px; }
.mobile-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 20px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  font-size: 11px;
  font-weight: 600;
}

.slide-actions {
  display: flex; align-items: center; justify-content: flex-end; gap: 4px;
  padding: 10px 12px; border-top: 1px solid #f1f5f9;
  background: #fafbfd;
}
.slide-action-btn {
  width: 32px; height: 32px; border-radius: 8px; border: 1px solid transparent;
  background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s; position: relative;
}
.slide-action-btn--neutral { color: #475569; }
.slide-action-btn--neutral:hover { background: rgba(71,85,105,.1); border-color: rgba(71,85,105,.2); }
.slide-action-btn--success { color: #16a34a; }
.slide-action-btn--success:hover { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.2); }
.slide-action-btn--warn { color: #d97706; }
.slide-action-btn--warn:hover { background: rgba(217,119,6,.1); border-color: rgba(217,119,6,.2); }
.slide-action-btn--primary { color: #4f46e5; }
.slide-action-btn--primary:hover { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.2); }
.slide-action-btn--danger { color: #dc2626; }
.slide-action-btn--danger:hover { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.2); }
.slide-action-btn:disabled { opacity: .5; cursor: not-allowed; }

/* ── Dialog ───────────────────────────────────────────── */
.dialog-card { border-radius: 16px !important; min-width: 420px; max-width: 540px; width: 100%; }
.dialog-card--campaign { max-width: 760px; }
.dialog-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 0;
}
.dialog-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.dialog-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #f1f5f9; color: #64748b; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.dialog-close:hover { background: #e2e8f0; }
.dialog-body { max-height: min(72vh, 760px); padding: 16px 24px; overflow-y: auto; }
.dialog-footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 12px 24px 20px; border-top: 1px solid #f1f5f9;
}
.dialog-btn {
  padding: 9px 20px; border-radius: 9px; font-size: 13px;
  font-weight: 600; border: none; cursor: pointer; transition: background .15s;
  display: inline-flex; align-items: center;
}
.dialog-btn--cancel { background: #f1f5f9; color: #64748b; }
.dialog-btn--cancel:hover { background: #e2e8f0; }
.dialog-btn--submit { background: #0d1b2a; color: #d4af37; }
.dialog-btn--submit:hover { background: #162333; }
.dialog-btn--danger { background: #dc2626; color: #fff; }
.dialog-btn--danger:hover { background: #b91c1c; }
.dialog-btn:disabled { opacity: .55; cursor: not-allowed; }

/* ── Dialog form fields ───────────────────────────────── */
.field-wrap {
  margin-bottom: 14px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 5px;
  letter-spacing: 0.2px;
}
.field-optional {
  font-weight: 400;
  color: #b0bec5;
  font-size: 11px;
  margin-left: 4px;
}

:deep(.dialog-input .q-field__control) {
  border-radius: 11px;
}
:deep(.dialog-input .q-field__control:before) {
  border-color: #e2e8f0;
}
:deep(.dialog-input .q-field__control:hover:before) {
  border-color: #0d1b2a;
}
:deep(.dialog-input.q-field--focused .q-field__control:before) {
  border-color: #d4af37 !important;
  border-width: 2px;
}
:deep(.dialog-input .q-field__prepend) {
  color: #b0bec5;
}
:deep(.dialog-input.q-field--focused .q-field__prepend) {
  color: #0d1b2a;
}
:deep(.dialog-input input::placeholder),
:deep(.dialog-input .q-field__native::placeholder) {
  color: #c8d0d8;
}

/* Toggle */
.toggle-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  padding: 6px 14px 6px 6px;
  width: 100%;
}
.toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.res-hint {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 10px 14px;
  background: rgba(212, 175, 55, 0.08);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 10px;
  font-size: 12.5px;
  color: #7a6420;
  margin-bottom: 16px;
  line-height: 1.5;
}
.res-hint .q-icon { color: #d4af37; flex-shrink: 0; margin-top: 1px; }
.res-hint strong { color: #5a4a10; font-weight: 700; }
.res-hint--mobile {
  background: rgba(59, 130, 246, 0.06);
  border-color: rgba(59, 130, 246, 0.22);
  color: #1e4490;
}
.res-hint--mobile .q-icon { color: #3b82f6; }
.res-hint--mobile strong { color: #1e3a8a; }
.creative-editor { margin: 12px 0 18px; padding: 16px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fbfcfe; }
.creative-editor__heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: #0f172a; font-size: 13px; }
.creative-editor__heading span { color: #64748b; font-size: 11px; }
.creative-editor__hint { margin: 5px 0 14px; color: #64748b; font-size: 11px; line-height: 1.5; }
.creative-preview { max-height: 180px; margin: 0 0 14px; border-radius: 8px; background: #e2e8f0; }
.creative-preview-label { display: block; margin-bottom: 5px; color: #64748b; font-size: 11px; font-weight: 600; }

/* ── Gallery ──────────────────────────────────────────── */
.dialog-card--gallery { max-width: 860px; }
.gallery-subtitle { margin-top: 2px; color: #64748b; font-size: 12px; }
.gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; }
.gallery-item { margin: 0; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #fbfcfe; }
.gallery-img { background: #e2e8f0; }
.gallery-caption { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; padding: 10px 12px; }
.gallery-placement { padding: 3px 8px; border-radius: 12px; color: #0d1b2a; background: rgba(212,175,55,.18); font-size: 11px; font-weight: 700; }
.gallery-device { display: inline-flex; align-items: center; gap: 3px; padding: 3px 8px; border-radius: 12px; color: #334155; background: #eef2f7; font-size: 10.5px; font-weight: 600; }
.gallery-device--mobile { color: #3b82f6; background: rgba(59,130,246,.08); }
.gallery-dimensions { margin-left: auto; color: #94a3b8; font-size: 10.5px; }
.remove-mobile-btn, .undo-remove-btn { padding: 0; border: 0; color: #dc2626; background: none; font-size: 11px; cursor: pointer; }
.undo-remove-btn { margin-left: 5px; text-decoration: underline; }

.delete-warning {
  display: flex; align-items: center;
  padding: 14px; background: rgba(239,68,68,.06);
  border: 1px solid rgba(239,68,68,.15); border-radius: 10px;
  font-size: 13.5px; color: #475569;
}

@media (max-width: 600px) {
  .campaign-filters { grid-template-columns: 1fr; }
  .dialog-card { min-width: 0; width: calc(100vw - 24px); }
  .dialog-body { padding: 14px 16px; }
}
</style>
